import React, { FC, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { HeaderGroup } from 'react-table';
import { IRowData } from 'constants/Table/interfaces';
import Input from '../../commons/Input/Input';
import { useAppDispatch } from '../../../redux/hooks';
import { Action } from '@reduxjs/toolkit';
import classNames from 'classnames';
import Icon from '../../commons/Icons';
import { ReactComponent as TableSettingsIcon } from '../../../assets/icons/new/UI/TableSettings.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/new/UI/Edit.svg';
import { ReactComponent as CloseCircleFilledIcon } from '../../../assets/icons/new/UI/CloseCircleFilled.svg';

interface TableShipmentsColumnProps {
    headerGroup: HeaderGroup<IRowData>;
    isNeedUpdateData: boolean;
    setIdFilter: (state: any) => {};
    setSkuFilter: (state: any) => {};
    setBarcodeFilter: (state: any) => {};
    setSizeFilter: (state: any) => {};
    setNameFilter: (state: any) => {};
    setBrandFilter: (state: any) => {};
    setNotesFilter: (state: any) => {};
    setGreaterThanZeroFilter?: (state: any) => {};
    idFilter: number | null;
    skuFilter: string | null;
    barcodeFilter: string | null;
    sizeFilter: string | null;
    nameFilter: string | null;
    brandFilter: string | null;
    notesFilter: string | null;
    greaterThanZeroFilter?: string | null;
    rowDataLoading: boolean;
    allColumns?: any[];
    selectedCount?: number;
    onSelectedActionsToggle?: () => void;
    setColumnOrder?: (order: string[]) => void;
    masterSelectState?: 0 | 1 | 2;
    onMasterSelectClick?: () => void;
}

const COLUMN_LABELS: Record<string, string> = {
    id: 'ID',
    sku: 'Артикул',
    barcode: 'Штрих-код',
    size: 'Размер',
    brand: 'Бренд',
    notes: 'Заметки',
    name: 'Наименование',
    reserved: 'В резерве',
    inStock: 'В наличии',
};

const MAIN_FIELDS = ['photo', 'barcode', 'size', 'sku', 'id', 'name', 'brand', 'notes', 'inStock', 'availableForPurchase', 'reserved', 'notDistributed'];
const TEXT_FIELDS = ['name', 'sku', 'id', 'barcode', 'brand', 'notes', 'size'];

const SortIcon: React.FC<{ isSorted: boolean; isSortedDesc?: boolean }> = ({ isSorted, isSortedDesc }) => {
    const activeCls = 'fill-[#2255a1] dark:fill-[#60a5fa]';
    const inactiveCls = 'fill-[#94a3b8] dark:fill-[#475569]';
    return (
        <span className={classNames(
            'ml-1 inline-flex flex-col items-center gap-[1px] transition-opacity shrink-0',
            isSorted ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'
        )}>
            <svg width="7" height="5" viewBox="0 0 7 5" fill="none">
                <path d="M3.5 1L6 4H1z" className={isSorted && !isSortedDesc ? activeCls : inactiveCls} />
            </svg>
            <svg width="7" height="5" viewBox="0 0 7 5" fill="none">
                <path d="M3.5 4L1 1h5z" className={isSorted && isSortedDesc ? activeCls : inactiveCls} />
            </svg>
        </span>
    );
};

const HeaderRow: FC<TableShipmentsColumnProps> = ({
    headerGroup,
    isNeedUpdateData,
    setIdFilter, setSkuFilter, setBarcodeFilter, setSizeFilter,
    setNameFilter, setBrandFilter, setNotesFilter, setGreaterThanZeroFilter,
    idFilter, skuFilter, barcodeFilter, sizeFilter, nameFilter, brandFilter, notesFilter,
    greaterThanZeroFilter,
    rowDataLoading,
    allColumns = [],
    selectedCount = 0,
    onSelectedActionsToggle,
    setColumnOrder,
    masterSelectState = 0,
    onMasterSelectClick,
}) => {
    const dispatch = useAppDispatch();
    const [colSettingsOpen, setColSettingsOpen] = useState(false);


    const draggedColId = useRef<string | null>(null);
    const [dragOverColId, setDragOverColId] = useState<string | null>(null);

    const handleDragStart = (colId: string) => { draggedColId.current = colId; };
    const handleDragOver = (e: React.DragEvent, colId: string) => {
        if (colId === 'selection' || colId === draggedColId.current) return;
        e.preventDefault();
        setDragOverColId(colId);
    };
    const handleDragLeave = (e: React.DragEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverColId(null);
    };
    const handleDrop = (e: React.DragEvent, targetId: string) => {
        e.preventDefault();
        const dragged = draggedColId.current;
        if (!dragged || dragged === targetId || targetId === 'selection' || !setColumnOrder) {
            draggedColId.current = null;
            setDragOverColId(null);
            return;
        }
        const currentIds = allColumns.map(c => c.id).filter(id => id !== 'selection');
        const fromIdx = currentIds.indexOf(dragged);
        const toIdx = currentIds.indexOf(targetId);
        if (fromIdx === -1 || toIdx === -1) { draggedColId.current = null; setDragOverColId(null); return; }
        const newOrder = [...currentIds];
        newOrder.splice(fromIdx, 1);
        newOrder.splice(toIdx, 0, dragged);
        setColumnOrder(newOrder);
        draggedColId.current = null;
        setDragOverColId(null);
    };
    const handleDragEnd = () => { draggedColId.current = null; setDragOverColId(null); };
    const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!colSettingsOpen) return;
        const handler = (e: MouseEvent) => {
            const inButton = buttonRef.current?.contains(e.target as Node);
            const inPopover = popoverRef.current?.contains(e.target as Node);
            if (!inButton && !inPopover) setColSettingsOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [colSettingsOpen]);

    const toggleableColumns = allColumns.filter(
        col => !['selection', 'photo'].includes(col.id)
    );

    const handleFilterChange = (fieldID: string, value: any) => {
        const map: Record<string, (state: any) => {}> = {
            id: setIdFilter, name: setNameFilter, barcode: setBarcodeFilter,
            size: setSizeFilter, sku: setSkuFilter, brand: setBrandFilter, notes: setNotesFilter,
        };
        const action = map[fieldID];
        if (action) dispatch(action(value) as Action<any>);
    };

    const getFilterValue = (fieldID: string): string => {
        const map: Record<string, any> = {
            id: idFilter, name: nameFilter, barcode: barcodeFilter,
            size: sizeFilter, sku: skuFilter, brand: brandFilter, notes: notesFilter,
        };
        return (map[fieldID] ?? '').toString();
    };

    // Parse warehouse column header: "WarehouseName\nSubtitle" or just a string
    const parseWarehouseHeader = (header: string) => {
        if (typeof header !== 'string') return { line1: String(header), line2: '' };
        const parts = header.split('\n');
        return { line1: parts[0] || '', line2: parts[1] || '' };
    };

    return (
        <>
            {/* Row 1: Column headers */}
            <tr {...headerGroup.getHeaderGroupProps()} style={{ height: 34 }}>
                {headerGroup.headers.map((column, i) => {
                    const isMain = MAIN_FIELDS.includes(column.id);
                    const isWh = column.id.startsWith('wh_');
                    const headerText = column.render('Header') as string;
                    const { line1, line2 } = parseWarehouseHeader(typeof headerText === 'string' ? headerText : '');
                    const isCenter = column.id.startsWith('wh_') || ['inStock', 'availableForPurchase', 'reserved', 'notDistributed'].includes(column.id);
                    const isRight = !isMain && !isCenter && !isWh && column.id !== 'selection' && column.id !== 'name' && column.id !== 'brand' && column.id !== 'notes';

                    return (
                        <th
                            className={classNames(
                                'group select-none py-1 normal-case',
                                'border-b border-slate-200 dark:border-slate-700',
                                i === 0 ? `sticky z-10 left-0 !bg-[#eef2ff] dark:!bg-[#0f172a]`
                                    : (column.id === 'photo') ? `sticky z-10 !bg-[#eef2ff] dark:!bg-[#0f172a]`
                                    : 'relative z-2',
                                isMain ? '' : 'min-w-[140px]',
                                column.id !== 'selection' ? 'cursor-grab' : '',
                            )}
                            data-sticky-td={column.id === 'selection' ? true : undefined}
                            {...column.getHeaderProps(column.getSortByToggleProps(
                                isMain || column.id === 'selection'
                                    ? { style: { minWidth: column.minWidth, width: column.width, maxWidth: column.maxWidth }, title: '' }
                                    : { style: { minWidth: 140, width: 140, maxWidth: 140 }, title: '' }
                            ))}
                            key={column.id}
                            draggable={column.id !== 'selection'}
                            onDragStart={column.id !== 'selection' ? () => handleDragStart(column.id) : undefined}
                            onDragOver={(e) => handleDragOver(e, column.id)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, column.id)}
                            onDragEnd={handleDragEnd}
                        >
                            {column.id === 'selection' ? (
                                <>
                                <div className="relative flex items-center justify-center h-full">
                                    <button
                                        ref={buttonRef}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (selectedCount > 0) {
                                                onSelectedActionsToggle?.();
                                            } else {
                                                if (!colSettingsOpen && buttonRef.current) {
                                                    setButtonRect(buttonRef.current.getBoundingClientRect());
                                                }
                                                setColSettingsOpen(v => !v);
                                            }
                                        }}
                                        title={selectedCount > 0 ? `Действия с выбранными (${selectedCount})` : 'Настройка колонок'}
                                        className={classNames(
                                            'h-[22px] flex items-center justify-center rounded transition-colors gap-[3px]',
                                            selectedCount > 0 ? 'px-1.5' : 'w-[22px]',
                                            colSettingsOpen
                                                ? 'bg-blue-100 text-[#2255a1] dark:bg-blue-900/40 dark:text-blue-400'
                                                : selectedCount > 0
                                                    ? 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/50'
                                                    : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700',
                                        )}
                                    >
                                        {selectedCount > 0 ? (
                                            <>
                                                <EditIcon width={13} height={13} className="shrink-0" style={{ fill: 'currentColor' }} />
                                                <span className="text-[11px] font-semibold leading-none">{selectedCount}</span>
                                            </>
                                        ) : (
                                            <TableSettingsIcon width={14} height={14} style={{ fill: 'currentColor' }} />
                                        )}
                                    </button>
                                </div>
                                {colSettingsOpen && buttonRect && ReactDOM.createPortal(
                                    <div
                                        ref={popoverRef}
                                        style={{
                                            position: 'fixed',
                                            top: buttonRect.bottom + 4,
                                            left: buttonRect.left,
                                            zIndex: 1000,
                                        }}
                                        className={`w-[220px] rounded-[6px] shadow-xl border bg-[#eef2ff] dark:bg-[#1e293b] border-[#d3ddec] dark:border-[#153c76]`}
                                        onClick={e => e.stopPropagation()}
                                    >
                                        <div className={`px-3 pt-2 pb-1 text-[12px] font-normal border-b border-[#d3ddec] dark:border-slate-700 text-[#1e293b] dark:text-[#d3ddec]`}>
                                            Видимость колонок
                                        </div>
                                        <div className={`py-1 max-h-[300px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 dark:scrollbar-thumb-slate-600 dark:scrollbar-track-slate-800`}>
                                            {toggleableColumns.map(col => {
                                                const label = COLUMN_LABELS[col.id] ||
                                                    (typeof col.Header === 'string'
                                                        ? col.Header.slice(0, 28)
                                                        : col.id);
                                                return (
                                                    <label
                                                        key={col.id}
                                                        className={`flex items-center gap-2 px-3 py-[5px] cursor-pointer group transition-colors hover:bg-white dark:hover:bg-[#0f172a]`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={col.isVisible}
                                                            onChange={() => col.toggleHidden()}
                                                            className="sr-only"
                                                        />
                                                        <div className={classNames(
                                                            'w-[14px] h-[14px] flex items-center justify-center border rounded-[3px] shrink-0 transition-colors',
                                                            col.isVisible
                                                                ? 'bg-[#2255a1] border-[#2255a1]'
                                                                : 'border-gray-300 group-hover:border-gray-400 dark:border-slate-600 dark:group-hover:border-slate-400',
                                                        )}>
                                                            {col.isVisible && (
                                                                <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                                                                    <path d="M1 3.5l2.5 2.5L8 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                                </svg>
                                                            )}
                                                        </div>
                                                        <span className={classNames(
                                                            'text-[12px] flex-1 min-w-0 truncate',
                                                            col.isVisible ? 'text-slate-700 dark:text-[#d3ddec]' : 'text-slate-400',
                                                        )}>
                                                            {label}
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>,
                                    document.body
                                )}
                                </>
                            ) : isWh ? (
                                /* 2-line warehouse header: icon + name / type — сжат под строку 34px */
                                <div className="flex flex-col items-start px-1 overflow-hidden">
                                    <div className="flex items-center gap-[3px] w-full min-w-0 leading-[14px]">
                                        {(column as any).whService && (
                                            <Icon
                                                marketSource={(column as any).whService}
                                                width={14} height={14}
                                                customClassName="flex-shrink-0 rounded-[2px]"
                                            />
                                        )}
                                        <span
                                            className={classNames(
                                                'text-[9pt] font-normal truncate',
                                                column.isSorted ? 'text-c-blue' : 'text-slate-700 dark:text-slate-200',
                                            )}
                                            title={line1}
                                        >
                                            {line1}
                                        </span>
                                        {column.canSort && <SortIcon isSorted={column.isSorted} isSortedDesc={column.isSortedDesc} />}
                                    </div>
                                    <div className="text-[8pt] leading-[11px] text-slate-400 dark:text-slate-500 truncate w-full">
                                        {(column as any).whTypeLabel || line2}
                                    </div>
                                </div>
                            ) : (
                                <div className={classNames(
                                    'flex items-center px-1',
                                    isCenter ? 'justify-center' : isRight ? 'justify-end' : 'justify-start',
                                )}>
                                    <span className={classNames(
                                        'text-[12px] font-normal truncate',
                                        column.isSorted ? 'text-c-blue' : 'text-slate-700 dark:text-slate-200',
                                    )}>
                                        {column.render('Header')}
                                    </span>
                                    {column.canSort && (
                                        <SortIcon isSorted={column.isSorted} isSortedDesc={column.isSortedDesc} />
                                    )}
                                </div>
                            )}
                            {dragOverColId === column.id && column.id !== 'selection' && (
                                <div className="absolute left-0 top-0 h-full w-[3px] bg-blue-500 z-30 pointer-events-none rounded-r" />
                            )}
                            {(column as any).canResize && column.id !== 'selection' && (
                                <div
                                    {...(column as any).getResizerProps?.()}
                                    onClick={e => e.stopPropagation()}
                                    className={classNames(
                                        'absolute right-0 top-0 h-full w-[4px] z-20 cursor-col-resize select-none touch-none transition-colors',
                                        (column as any).isResizing
                                            ? ('bg-[#153c76] dark:bg-[#eef2ff]')
                                            : ('bg-transparent hover:bg-[#153c76]/40 dark:hover:bg-[#eef2ff]/40'),
                                    )}
                                />
                            )}
                        </th>
                    );
                })}
            </tr>

            {/* Row 2: Column search inputs */}
            <tr {...headerGroup.getHeaderGroupProps()} key={`${headerGroup.id}_search`} className={'bg-blue-50 dark:bg-[#1e293b] border-b border-slate-200 dark:border-slate-700'} style={{ height: 34 }}>
                {headerGroup.headers.map((column) => {
                    const isColSticky = (column as any).sticky === 'left';
                    const stickyStyle = isColSticky ? { left: (column as any).totalLeft } : undefined;

                    if (column.id === 'selection') {
                        return (
                            <td key={column.id} className={`sticky left-0 z-10 bg-blue-50 dark:bg-[#1e293b]`} data-sticky-td>
                                {onMasterSelectClick && (
                                    <div className="flex items-center justify-center h-full">
                                        <label
                                            className="inline-flex cursor-pointer"
                                            title={masterSelectState === 0 ? 'Выбрать страницу' : masterSelectState === 1 ? 'Выбрать всё' : 'Снять выбор'}
                                        >
                                            <span className="sr-only">Выбрать страницу</span>
                                            <input
                                                type="checkbox"
                                                className={`form-checkbox ${masterSelectState === 2 ? 'form-checkbox-all' : ''}`}
                                                checked={masterSelectState >= 1}
                                                onChange={() => onMasterSelectClick()}
                                            />
                                        </label>
                                    </div>
                                )}
                            </td>
                        );
                    }
                    if (column.id === 'photo') {
                        return (
                            <td
                                key={column.id}
                                className={`sticky z-10 bg-blue-50 dark:bg-[#1e293b]`}
                                style={{ left: (column as any).totalLeft ?? 34 }}
                                data-sticky-td
                            />
                        );
                    }

                    if (TEXT_FIELDS.includes(column.id)) {
                        const isRight = ['id', 'barcode'].includes(column.id);
                        const val = getFilterValue(column.id);
                        return (
                            <td
                                key={column.id}
                                className={`px-1 py-0 bg-blue-50 dark:bg-[#1e293b]`}
                                data-sticky-td={isColSticky || undefined}
                                style={stickyStyle}
                            >
                                <div className="relative">
                                    <input
                                        type="text"
                                        defaultValue={val}
                                        key={val}
                                        disabled={rowDataLoading}
                                        className={classNames(
                                            'w-full h-[26px] text-[12px] px-2 rounded-[4px] border border-transparent focus:outline-none transition-colors',
                                            'bg-blue-100 dark:bg-[#0f172a] text-slate-700 dark:text-[#d3ddec] placeholder:text-slate-400 dark:placeholder:text-slate-600 hover:bg-white dark:hover:bg-[#0a1020] focus:bg-white dark:focus:bg-[#0a1020] focus:border-c-blue dark:focus:border-[#2255a1]',
                                            rowDataLoading ? 'opacity-50' : '',
                                            isRight ? 'text-right' : 'text-left',
                                        )}
                                        placeholder="..."
                                        onBlur={(e) => handleFilterChange(column.id, e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                (e.target as HTMLInputElement).blur();
                                            }
                                        }}
                                    />
                                    {val && (
                                        <button
                                            className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 leading-none"
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                handleFilterChange(column.id, '');
                                            }}
                                        >
                                            <CloseCircleFilledIcon width={14} height={14} style={{ fill: 'currentColor' }} />
                                        </button>
                                    )}
                                </div>
                            </td>
                        );
                    }

                    if (
                        (['reserved'].includes(column.id) || (window.location.pathname.includes('stocks-reserve') && column.id.includes('wh_')))
                        && setGreaterThanZeroFilter
                    ) {
                        const isSelected = greaterThanZeroFilter?.includes(column.id);
                        return (
                            <td
                                key={column.id}
                                className="px-1 text-right text-c-blue cursor-pointer"
                                onClick={() => dispatch(setGreaterThanZeroFilter(greaterThanZeroFilter && isSelected ? null : column.id) as Action<any>)}
                            >
                                <button
                                    className={classNames(
                                        'line-through border-c-blue text-[12px]',
                                        isSelected ? 'border-b font-medium' : 'hover:border-b font-normal',
                                    )}
                                    style={{ lineHeight: 1 }}
                                >
                                    &nbsp;0&nbsp;
                                </button>
                                {isSelected && <span className="ml-1 text-[11px]">✕</span>}
                            </td>
                        );
                    }

                    return <td key={column.id} id={column.id} className="bg-blue-50 dark:bg-[#1e293b]" />;
                })}
            </tr>
        </>
    );
};

export default HeaderRow;
